<template>
  <div class="sidebar">
    <!-- Backdrop for mobile -->
    <div class="sidebar-backdrop" v-if="isNavOpen"></div>

    <!-- Main Sidebar -->
    <transition name="slide">
      <div
        id="sidebar-scrollbar"
        :class="[isNavOpen ? 'sidebar-panel' : 'sidebar-sm', 'sidebar-wrapper']"
      >
        <!-- Brand header -->
        <div class="rail-head">
          <div class="rail-brand">
            <img
              v-if="userInfo && userInfo.bussinessLogoUrl"
              :src="userInfo.bussinessLogoUrl"
              alt="logo"
              class="rail-logo"
            />
            <span v-else class="rail-mark mdi mdi-hospital-box-outline"></span>
          </div>
          <button
            class="rail-collapse"
            @click="toggleNav"
            :title="isNavOpen ? 'Collapse menu' : 'Expand menu'"
          >
            <span
              class="mdi"
              :class="
                isNavOpen
                  ? 'mdi-chevron-double-left'
                  : 'mdi-chevron-double-right'
              "
            ></span>
          </button>
        </div>

        <!-- Search -->
        <div class="rail-search" v-if="isNavOpen">
          <span class="mdi mdi-magnify rail-search-icon"></span>
          <input
            type="text"
            class="rail-search-input"
            v-model="searchQuery"
            @input="filterMenuItems"
            placeholder="Search menu…"
          />
          <button
            v-if="searchQuery"
            class="rail-search-clear"
            @click="clearSearch"
            title="Clear search"
          >
            <span class="mdi mdi-close"></span>
          </button>
        </div>

        <!-- Navigation Items -->
        <nav class="sidebar-nav" :key="`sidebar-${userRole}`">
          <div
            v-for="(section, sectionKey) in groupedSidebar"
            :key="sectionKey"
            class="nav-section"
            v-show="hasAccessToSection(section)"
          >
            <h4 class="nav-section-title" v-if="isNavOpen">{{ sectionKey }}</h4>

            <div
              v-for="(item, index) in section"
              v-show="!item.disable"
              :key="index + item.name"
              class="nav-item"
            >
              <!-- Menu Item with Submenu -->
              <div
                v-if="!item.path || item.submenu"
                @click="handleSubmenu(item)"
                :class="[
                  'nav-link',
                  { 'has-submenu': item.submenu },
                  { active: item.submenu && rotate && item.name === dropdown }
                ]"
                :title="item.description || item.name"
              >
                <div class="nav-link-content">
                  <span :class="`nav-icon mdi ${item.icon}`"></span>
                  <span class="nav-text" v-if="isNavOpen">{{ item.name }}</span>
                </div>
                <span
                  v-if="item.submenu"
                  :class="[
                    'chevron-icon',
                    { rotated: rotate && item.name === dropdown }
                  ]"
                >
                  <i class="mdi mdi-chevron-right"></i>
                </span>
              </div>

              <!-- Regular Menu Item -->
              <router-link
                v-if="item.path && !item.submenu"
                :to="item.path"
                class="nav-link"
                :title="item.description || item.name"
                active-class="active"
                exact
              >
                <div class="nav-link-content">
                  <span :class="`nav-icon mdi ${item.icon}`"></span>
                  <span class="nav-text" v-if="isNavOpen">{{ item.name }}</span>
                </div>
              </router-link>

              <!-- Submenu Items -->
              <transition name="submenu">
                <div
                  v-if="item.name === dropdown && item.submenu && rotate"
                  class="submenu-container"
                >
                  <router-link
                    v-for="(subItem, subIndex) in getEnabledSubmenuItems(item)"
                    :key="subIndex"
                    :to="subItem.path"
                    class="submenu-link"
                    active-class="active"
                    exact
                    :title="subItem.description || subItem.name"
                  >
                    <span :class="`submenu-icon mdi ${subItem.icon}`"></span>
                    <span class="submenu-text" v-if="isNavOpen">{{
                      subItem.name
                    }}</span>
                  </router-link>
                </div>
              </transition>
            </div>
          </div>
        </nav>

        <!-- Sidebar Footer — account + dropup -->
        <div class="sidebar-footer" v-if="isNavOpen" ref="account">
          <transition name="dropup">
            <div class="account-menu" v-if="showAccountMenu">
              <button class="account-menu-item" @click="goToProfile">
                <span class="mdi mdi-account-outline"></span>
                <span>Profile</span>
              </button>
              <button
                class="account-menu-item account-menu-item--danger"
                @click="signOut"
              >
                <span class="mdi mdi-logout"></span>
                <span>Sign out</span>
              </button>
            </div>
          </transition>

          <button
            class="account-trigger"
            :class="{ 'is-open': showAccountMenu }"
            @click="toggleAccountMenu"
          >
            <div class="user-avatar">
              <img
                :src="userProfileImage"
                alt="User Avatar"
                class="user-avatar-img"
              />
            </div>
            <div class="user-details">
              <p class="user-name">{{ userInfo && userInfo.full_name }}</p>
              <p class="user-role">{{ getUserRole }}</p>
            </div>
            <span class="account-caret mdi mdi-chevron-up"></span>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { generateSidebar } from "../utils/sidebar";

export default {
  data() {
    return {
      rotate: false,
      dropdown: "",
      searchQuery: "",
      filteredSidebar: null,
      showAccountMenu: false
    };
  },
  methods: {
    ...mapActions({
      toggleNav: "toggleNav",
      validateSession: "auth/validateSession"
    }),
    handleSubmenu(val) {
      this.dropdown = val.name;

      if (val && val.submenu) {
        this.rotate = !this.rotate;
      } else {
        this.rotate = false;
      }
    },
    filterMenuItems() {
      if (!this.searchQuery) {
        this.filteredSidebar = null;
        return;
      }

      this.filteredSidebar = this.getSidebar.filter(item => {
        const matchesSearch = item.name
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase());
        if (item.submenu) {
          return (
            matchesSearch ||
            item.submenu.some(subItem =>
              subItem.name
                .toLowerCase()
                .includes(this.searchQuery.toLowerCase())
            )
          );
        }
        return matchesSearch;
      });
    },
    clearSearch() {
      this.searchQuery = "";
      this.filteredSidebar = null;
    },
    getEnabledSubmenuItems(item) {
      if (!item.submenu) return [];
      return item.submenu.filter(subItem => !subItem.disable);
    },
    toggleAccountMenu() {
      this.showAccountMenu = !this.showAccountMenu;
    },
    goToProfile() {
      this.showAccountMenu = false;
      if (this.$route.path !== "/profile") {
        this.$router.push("/profile");
      }
    },
    async signOut() {
      this.showAccountMenu = false;
      const payload = {
        userId: this.userInfo.id,
        sessionId:
          this.userInfo.currentUserSession &&
          this.userInfo.currentUserSession.sessionId
      };
      await this.validateSession({ vm: this, payload });
      this.$store.dispatch("auth/logout", { vm: this }).then(() => {
        this.$router.push("/");
      });
    },
    handleClickOutside(e) {
      if (
        this.showAccountMenu &&
        this.$refs.account &&
        !this.$refs.account.contains(e.target)
      ) {
        this.showAccountMenu = false;
      }
    },
    hasAccessToSection(section) {
      if (!section || !section.length) return true; // Show all sections if no items
      return section.some(item => !item.disable);
    }
  },
  beforeCreate() {
    this.$store.commit("initialiseStore");
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  watch: {
    role: {
      handler() {
        // Force re-render when role changes
        this.$forceUpdate();
      },
      deep: true
    },
    userInfo: {
      handler() {
        // Force re-render when user info changes
        this.$forceUpdate();
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters({
      isNavOpen: "isNavOpen",
      role: "auth/role",
      userInfo: "auth/userInfo"
    }),
    userRole() {
      const role = this.role || [];
      if (role && role.length > 1) {
        return "Super Admin";
      } else {
        return role[0] && role[0].name;
      }
    },
    getUserRole() {
      const role = this.role || [];
      if (role && role.length > 1) {
        return "Super Admin";
      } else {
        return role[0] && role[0].name;
      }
    },
    userProfileImage() {
      return this.userInfo && this.userInfo.profile
        ? this.userInfo.profile
        : require("@/assets/upload.png");
    },
    getSidebar() {
      return generateSidebar();
    },
    groupedSidebar() {
      const grouped = {};
      const sidebarItems = this.filteredSidebar || this.getSidebar;

      sidebarItems.forEach(item => {
        const sectionKey = item.section || "General";
        if (!grouped[sectionKey]) {
          grouped[sectionKey] = [];
        }
        grouped[sectionKey].push(item);
      });

      // Filter out sections that have no accessible items
      const filteredGrouped = {};
      Object.keys(grouped).forEach(sectionKey => {
        const section = grouped[sectionKey];
        if (section.some(item => !item.disable)) {
          filteredGrouped[sectionKey] = section;
        }
      });

      return filteredGrouped;
    }
  }
};
</script>

<style scoped>
/* =======================================================================
   Sidebar — Graphite-violet enterprise rail (Option B)
   Deep brand field, explicit light text on every element (so the global
   span/a rules in main.css can never wash anything out), one signature:
   the active item (violet fill + accent indicator + bright icon).
   ===================================================================== */

.sidebar {
  position: relative;
  z-index: 1000;
}

.sidebar-wrapper {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  background: linear-gradient(
    184deg,
    rgba(36, 28, 67, 0.9) 0%,
    rgba(26, 19, 54, 0.92) 100%
  );
  -webkit-backdrop-filter: blur(22px) saturate(150%);
  backdrop-filter: blur(22px) saturate(150%);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: var(--shadow-md), inset 0 1px 0 rgba(255, 255, 255, 0.08),
    inset -1px 0 0 rgba(255, 255, 255, 0.04);
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  transition: width var(--dur) var(--ease);
  z-index: 1000;
}

.sidebar-panel {
  width: 236px;
  margin-top: 0;
}

.sidebar-sm {
  width: 72px;
  margin-top: 0;
}

/* Scrollbar */
#sidebar-scrollbar::-webkit-scrollbar {
  width: 6px;
}
#sidebar-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
#sidebar-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.16);
  border-radius: 10px;
}
#sidebar-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.26);
}

/* Brand header */
.rail-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 14px 10px;
}
.rail-brand {
  display: flex;
  align-items: center;
  min-width: 0;
}
.rail-logo {
  height: auto;
  max-height: 30px;
  max-width: 168px;
  object-fit: contain;
  background: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  display: block;
}
.rail-mark {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 1.25rem;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
}
.rail-collapse {
  margin-left: auto;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  border: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.72);
  font-size: 1.125rem;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);
}
.rail-collapse .mdi {
  color: rgba(255, 255, 255, 0.72);
}
.rail-collapse:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}
.rail-collapse:hover .mdi {
  color: #fff;
}

/* Search */
.rail-search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 12px 8px;
  padding: 0 10px;
  height: 36px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: var(--radius-md);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: border-color var(--dur) var(--ease),
    background var(--dur) var(--ease);
}
.rail-search:focus-within {
  border-color: var(--accent-500);
  background: rgba(255, 255, 255, 0.12);
}
.rail-search-icon {
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.125rem;
}
.rail-search-input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #fff;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
}
.rail-search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}
.rail-search-clear {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1rem;
  display: flex;
}
.rail-search-clear:hover {
  color: #fff;
}

/* Navigation */
.sidebar-nav {
  flex: 1;
  padding: 2px 0 8px;
  margin-top: 0.25rem;
}
.nav-section {
  margin-bottom: 0.45rem;
}
.nav-section-title {
  font-size: var(--fs-eyebrow);
  font-weight: 700;
  color: rgba(255, 255, 255, 0.42);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0 1.1rem 0.3rem;
  padding: 0;
}

.nav-item {
  margin-bottom: 0;
}
.nav-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 12px;
  margin: 1px 8px;
  border-radius: var(--radius-md);
  color: rgba(255, 255, 255, 0.72);
  text-decoration: none;
  font-size: var(--fs-body);
  font-weight: 500;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: color var(--dur) var(--ease),
    background-color var(--dur) var(--ease);
}
.nav-link::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 0;
  width: 3px;
  background: var(--accent-500);
  border-radius: 0 3px 3px 0;
  transition: height var(--dur) var(--ease);
}
.nav-link:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
}
.nav-link:hover::before {
  height: 40%;
}
.nav-link:hover .nav-icon {
  color: var(--accent-500);
}
.nav-link.active {
  color: #fff;
  background: rgba(109, 84, 199, 0.28);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
}
.nav-link.active::before {
  height: 60%;
}
.nav-link.active .nav-icon {
  color: #b9a8f0;
}

.nav-link-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.nav-icon {
  color: inherit;
  font-size: 1.2rem;
  width: 22px;
  min-width: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color var(--dur) var(--ease);
}
.nav-text {
  color: inherit;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.chevron-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: rgba(255, 255, 255, 0.5);
  transition: transform var(--dur) var(--ease), color var(--dur) var(--ease);
}
.chevron-icon.rotated {
  transform: rotate(90deg);
  color: var(--accent-500);
}

/* Submenu */
.submenu-container {
  margin: 2px 0 4px 1.5rem;
  padding-left: 0.85rem;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}
.submenu-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 11px;
  margin: 1px 0;
  border-radius: var(--radius-sm);
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  font-size: var(--fs-sm);
  font-weight: 500;
  transition: color var(--dur) var(--ease),
    background-color var(--dur) var(--ease);
}
.submenu-link:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
}
.submenu-link.active {
  color: #fff;
  background: rgba(109, 84, 199, 0.28);
}
.submenu-icon {
  color: inherit;
  font-size: 1rem;
  width: 20px;
  min-width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.submenu-text {
  color: inherit;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Footer — account + dropup */
.sidebar-footer {
  position: relative;
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.account-trigger {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.06);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  cursor: pointer;
  text-align: left;
  transition: background var(--dur) var(--ease),
    border-color var(--dur) var(--ease);
}
.account-trigger:hover,
.account-trigger.is-open {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.16);
}
.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.12);
}
.user-avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
.user-details {
  flex: 1;
  min-width: 0;
}
.user-name {
  font-size: var(--fs-sm);
  font-weight: 600;
  color: #fff;
  margin: 0;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-role {
  font-size: var(--fs-xs);
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
  line-height: 1.3;
  text-transform: capitalize;
}
.account-caret {
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.125rem;
  transition: transform var(--dur) var(--ease), color var(--dur) var(--ease);
}
.account-trigger.is-open .account-caret {
  color: #fff;
  transform: rotate(180deg);
}

/* Dropup menu (light popover on the dark rail) */
.account-menu {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: calc(100% - 6px);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  padding: 6px;
  z-index: 20;
}
.account-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 11px;
  border: 0;
  background: transparent;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: 500;
  color: var(--text);
  text-align: left;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);
}
.account-menu-item .mdi {
  font-size: 1.125rem;
  color: var(--muted);
}
.account-menu-item:hover {
  background: var(--surface-2);
}
.account-menu-item--danger,
.account-menu-item--danger .mdi {
  color: var(--danger);
}

/* Dropup transition */
.dropup-enter-active,
.dropup-leave-active {
  transition: opacity var(--dur) var(--ease), transform var(--dur) var(--ease);
}
.dropup-enter,
.dropup-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* Collapsed state */
.sidebar-sm .rail-head {
  justify-content: center;
  padding: 14px 0 10px;
}
.sidebar-sm .rail-logo {
  display: none;
}
.sidebar-sm .rail-collapse {
  margin: 0 auto;
}
.sidebar-sm .rail-collapse .mdi {
  color: rgba(255, 255, 255, 0.85);
}
.sidebar-sm .rail-search,
.sidebar-sm .nav-section-title,
.sidebar-sm .nav-text,
.sidebar-sm .submenu-text,
.sidebar-sm .sidebar-footer {
  display: none;
}
.sidebar-sm .nav-link {
  padding: 10px;
  margin: 2px 12px;
  justify-content: center;
}
.sidebar-sm .nav-link-content {
  justify-content: center;
}
.sidebar-sm .nav-icon {
  font-size: 1.35rem;
}
.sidebar-sm .chevron-icon,
.sidebar-sm .submenu-container {
  display: none;
}

/* Collapsed tooltip */
.sidebar-sm .nav-link:hover::after {
  content: attr(title);
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 10px;
  background: var(--ink-900);
  color: #fff;
  padding: 5px 10px;
  border-radius: var(--radius-sm);
  font-size: var(--fs-xs);
  font-weight: 500;
  white-space: nowrap;
  z-index: 1001;
  box-shadow: var(--shadow-md);
}

/* Focus */
.nav-link:focus-visible,
.submenu-link:focus-visible,
.rail-collapse:focus-visible,
.rail-search-clear:focus-visible {
  outline: 2px solid var(--accent-500);
  outline-offset: -2px;
}

/* Notification badge (status) */
.nav-link .notification-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  background: var(--danger);
  border-radius: 50%;
}

/* Transitions */
.submenu-enter-active,
.submenu-leave-active {
  transition: opacity 0.22s var(--ease), transform 0.22s var(--ease);
}
.submenu-enter,
.submenu-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s var(--ease);
}
.slide-enter,
.slide-leave-to {
  transform: translateX(-100%);
}

/* Responsive */
@media (max-width: 768px) {
  .sidebar-panel {
    width: 100%;
    max-width: 300px;
  }
  .sidebar-sm {
    width: 72px;
  }
}
@media (max-width: 480px) {
  .sidebar-panel {
    width: 100%;
    max-width: 100%;
  }
  .sidebar-sm {
    width: 64px;
  }
}
</style>
